/**
 * 用户级元数据缓存 (内存 + localStorage 双层)
 *
 * 用于缓存「用户独有、几乎不变」的字段, 减少重复接口请求:
 * - 资金密码是否设置 (单调, 只缓存 true)
 * - 后续可扩展: VIP 等级、邀请码、银行卡是否绑过, etc.
 *
 * 自动按 userStore.memberId 隔离, 多账号切换互不影响.
 * 当前不在 logout 时主动清理 (key 已按 userId 区分, 不会串).
 */

import { useUserStore } from '@/stores'

const memCache = new Map<string, unknown>()

function fullKey(scope: string, userId: string): string {
  return `uc:${userId}:${scope}`
}

export function getCached<T>(scope: string): T | undefined {
  const userStore = useUserStore()
  if (!userStore.memberId) return undefined
  const key = fullKey(scope, userStore.memberId)
  if (memCache.has(key)) return memCache.get(key) as T
  const raw = localStorage.getItem(key)
  if (raw === null) return undefined
  try {
    const parsed = JSON.parse(raw) as T
    memCache.set(key, parsed)
    return parsed
  } catch {
    return undefined
  }
}

export function setCached<T>(scope: string, value: T): void {
  const userStore = useUserStore()
  if (!userStore.memberId) return
  const key = fullKey(scope, userStore.memberId)
  memCache.set(key, value)
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* localStorage 写满 / 隐私模式: 内存层仍有值, 忽略 */
  }
}

export function clearCachedScope(scope: string): void {
  const userStore = useUserStore()
  if (!userStore.memberId) return
  const key = fullKey(scope, userStore.memberId)
  memCache.delete(key)
  localStorage.removeItem(key)
}
